import type { ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  INSPECTION_CAMERA,
  WATER_HEATER,
} from "@volca/ui/components/ui/empty-machine";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import { Stack } from "@volca/ui/components/ui/stack";
import { useCanManageProfitability } from "@/profitability/access";
import { RUN_STATUS } from "@/profitability/constants";
import type { ProfitabilityRun } from "@/profitability/types";
import { useRerunReport } from "@/profitability/use-rerun-report";
import { runDates } from "@/profitability/utils";

export const RunContent = ({
  businessId,
  run,
  children,
}: {
  businessId: string;
  run: ProfitabilityRun;
  children: ReactNode;
}) => {
  const { rerun, rerunning } = useRerunReport(businessId, run);
  // Building a period again is finance's (Profitability::Access).
  const canRerun = useCanManageProfitability() && !run.draft_sample;
  if (run.status === RUN_STATUS.failed)
    return (
      <ErrorState
        retrying={rerunning}
        title="This report could not be built"
        description={
          run.error?.message ??
          "Something went wrong while building this report."
        }
      >
        {canRerun && (
          <Button
            onClick={() => rerun()}
            disabled={rerunning}
            loading={rerunning}
            loadingLabel={`${runDates(run) ? "Try these dates again" : "Try this report again"}, in progress`}
          >
            {runDates(run) ? "Try these dates again" : "Try this report again"}
          </Button>
        )}
        {run.draft_sample && (
          <span>Return to the agent to rebuild this draft.</span>
        )}
      </ErrorState>
    );

  if (run.status !== RUN_STATUS.completed)
    return (
      <Stack role="status" align="stretch" className="w-full flex-1">
        <MachineEmptyState
          art={WATER_HEATER}
          layout="workspace"
          active={run.status === RUN_STATUS.running}
          title={
            run.status === RUN_STATUS.pending
              ? "Your report is queued"
              : "Building your report"
          }
          description={
            "Volca is reading your documents and running the numbers. You can leave this page. The report keeps building."
          }
        />
      </Stack>
    );

  if (!run.workbook_available)
    return (
      <MachineEmptyState
        art={INSPECTION_CAMERA}
        layout="workspace"
        title="The results are no longer available"
        description="This report finished, but its workbook has since been removed. Building these dates again produces a fresh copy."
      >
        {canRerun && (
          <Button
            onClick={() => rerun()}
            disabled={rerunning}
            loading={rerunning}
            loadingLabel="Build it again, in progress"
          >
            Build it again
          </Button>
        )}
      </MachineEmptyState>
    );

  return children;
};
